import { AppNS } from 'app';

export const queryKeys = {
  auth: {
    all: () => ['auth'] as const,
  },

  settings: {
    all: () => ['settings'] as const,
  },

  notifications: {
    all: () => ['notifications'] as const,
    list: () => [...queryKeys.notifications.all(), 'list'] as const,
    panel: () => [...queryKeys.notifications.all(), 'panel'] as const,
  },

  applications: {
    all: () => ['applications'] as const,
    legacy: () => ['applications/get'] as const,
    lists: () => [...queryKeys.applications.all(), 'list'] as const,
    list: (p: string) => [...queryKeys.applications.lists(), p] as const,
    details: () => [...queryKeys.applications.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.applications.details(), id] as const,
    reviews: (id: string) => [...queryKeys.applications.detail(id), 'reviews'] as const,
    logs: (id: string) => ['application', id, 'logs'] as const,
    csv: (p: object | null) => [...queryKeys.applications.all(), 'csv', p] as const,
  },

  elevatorProjects: {
    all: () => ['elevatorProjects'] as const,
    legacy: () => ['elevatorProjects/get'] as const,
    lists: () => [...queryKeys.elevatorProjects.all(), 'list'] as const,
    agencies: () => [...queryKeys.elevatorProjects.all(), 'agencies'] as const,
    detail: (id: string) => [...queryKeys.elevatorProjects.all(), id] as const,
    projectAgencies: (projectId: string) => [...queryKeys.elevatorProjects.detail(projectId), 'agencies'] as const,
    projectAgencyInvitations: (projectId: string) =>
      [...queryKeys.elevatorProjects.projectAgencies(projectId), 'invitations'] as const,
    projectInvitations: (projectId: string) =>
      [...queryKeys.elevatorProjects.detail(projectId), 'invitations'] as const,
    inspectionEvents: (projectId: string) =>
      [...queryKeys.elevatorProjects.detail(projectId), 'inspectionEvents'] as const,
    inspectionEventsReadyToInvoice: (projectId: string) =>
      [...queryKeys.elevatorProjects.detail(projectId), 'inspectionEventsReadyToInvoice'] as const,
    inspectionNeeds: (projectId: string) =>
      [...queryKeys.elevatorProjects.detail(projectId), 'inspectionNeeds'] as const,
    paymentsRequests: (projectId: string) =>
      [...queryKeys.elevatorProjects.detail(projectId), 'paymentsRequests'] as const,
    paymentRequests: (projectId: string) =>
      [...queryKeys.elevatorProjects.detail(projectId), 'paymentRequests'] as const,
    paymentRequest: (projectId: string, paymentRequestId: string) =>
      [...queryKeys.elevatorProjects.paymentRequests(projectId), paymentRequestId] as const,
    certificatePayments: (projectId: string) =>
      [...queryKeys.elevatorProjects.detail(projectId), 'certificatePayments'] as const,
    equipments: (projectId: string) => [...queryKeys.elevatorProjects.detail(projectId), 'equipments'] as const,
    staff: (projectId: string) => [...queryKeys.elevatorProjects.detail(projectId), 'staff'] as const,
  },

  elevatorReports: {
    all: () => ['elevatorReports'] as const,
    detail: (id: string) => [...queryKeys.elevatorReports.all(), id] as const,
    equipmentInspections: (id: string) => [...queryKeys.elevatorReports.detail(id), 'equipmentInspections'] as const,
  },

  equipment: {
    detail: (id: string) => ['equipment', id] as const,
    versions: (id: string) => [...queryKeys.equipment.detail(id), 'versions'] as const,
    documents: (id: string) => ['equipment/documents', id] as const,
  },

  inspections: {
    all: () => ['inspections/get'] as const,
    backofficeList: (searchParamsString: string) => ['backoffice-list-inspections/get', searchParamsString] as const,
    agencyList: (searchParamsString: string, sectionId: string) =>
      ['agency-list-inspections/get', searchParamsString, sectionId] as const,
    dcraAdminList: (sectionId: string, searchParamsString: string) =>
      ['admin-list-dcra-inspections/get', sectionId, searchParamsString] as const,
    swoCheck: (id: string) => ['inspection/check-swo', id] as const,
    inspectionEvent: (inspectionId: string) => ['inspectionEvent', inspectionId] as const,
    checkIns: (inspectionId: string) => [...queryKeys.inspections.inspectionEvent(inspectionId), 'checkIns'] as const,
    events: (inspectorId: string, yearMonth: string) => ['inspection-events', inspectorId, yearMonth] as const,
  },

  agencies: {
    all: () => ['agencies'] as const,
    staff: (agencyCompanyId: string) => [...queryKeys.agencies.all(), 'staff', agencyCompanyId] as const,
    agencyStaff: (companyId: string | undefined) => ['agency', companyId, 'staff'] as const,
    detail: (id: string) => ['companies', id] as const,
    public: (id: string) => ['company-public', id] as const,
    inspectors: (companyId: string) => ['company-inspectors', companyId] as const,
    documentsHistory: (id: string, page: number) => ['company-documents-history/get', id, page] as const,
    filterInspectionAgencies: (inspectorId: string) => ['filter-inspection-agencies/get', inspectorId] as const,
    boList: (searchParamsString: string) => ['bo-agencies/get', searchParamsString] as const,
    boListAll: () => ['bo-agencies-list'] as const,
    boInspectors: (searchParamsString: string) => ['bo-agency-inspectors/get', searchParamsString] as const,
    agencyList: (searchParamsString: string) => ['agency-inspectors/get', searchParamsString] as const,
  },

  inspectors: {
    detail: (id: string) => ['inspectors', id] as const,
    byCompany: (companyId: string) => ['inspectors', companyId] as const,
    disciplines: (id: string) => ['inspectorDisciplines', id] as const,
    employment: (email: string) => ['inspector-employment/get', email] as const,
    certificates: (employmentId: string) => ['inspector-certificates/get', employmentId] as const,
    disciplineDocumentsHistory: (id: string, page: number) => ['discipline-documents-history/get', id, page] as const,
    staffRoster: (searchParamsString: string) => ['staff-roster/get', searchParamsString] as const,
    staffRosterAll: () => ['staff-roster/get'] as const,
    staffRosterForAgencyDisciplines: () => ['staff-roster/get', 'for-agency-disciplines'] as const,
    staffRosterInvitation: (invitationId: string) => ['staff-roster/get-invitation', invitationId] as const,
    reportInspectors: (companyId: string, staffRole: string, permitIds: string[]) =>
      ['report-inspectors', companyId, staffRole, ...permitIds] as const,
    adminDobList: (searchParamsString: string) => ['admin-list-dob-inspectors/get', searchParamsString] as const,
    userEmployments: (userId: string) => ['user-employments', userId] as const,
  },

  invitations: {
    byUser: (userId: string) => ['invitations', userId] as const,
    pendingByUserId: (userId: string) => ['pending-user-invitations/get', userId] as const,
    pendingByEmail: (email: string) => ['pending-user-invitations/get', email] as const,
  },

  tickets: {
    boList: (searchParamsString: string) => ['bo-tickets/get', searchParamsString] as const,
    boListEmpty: () => ['bo-tickets/get', ''] as const,
    detail: (id: string) => ['ticket-details', id] as const,
    comments: (ticketId: string) => ['ticket', ticketId, 'comments'] as const,
    logs: (id: string) => ['ticket', id, 'logs'] as const,
    agencyList: (searchParamsString: string) => ['agency-all/get', searchParamsString] as const,
    agencyListAll: () => ['agency-all/get'] as const,
    dobAdmins: () => ['get-dob-admins-select'] as const,
    disciplineValue: (disciplineId: string) => ['get-discipline-value', disciplineId] as const,
    reportForTicket: (eventReportId: string) => ['get-report-for-ticket', eventReportId] as const,
    disciplinesAutocomplete: (subject: string) => ['get-disciplines-autocomplete', subject] as const,
    companiesAutocomplete: (value: string) => ['get-companies-autocomplete', value] as const,
  },

  qaReports: {
    list: (searchParamsString: string, view: string, id: string, inspectorId: string) =>
      ['qa-reports/get', searchParamsString, view, id, inspectorId] as const,
    detail: (reportId: string) => ['qa-reports/get-by-id', reportId] as const,
    adminInspectionsList: (searchParamsString: string) =>
      ['qa-reports-for-admin-list-inspections/get', searchParamsString] as const,
    agencyInspectionsList: (searchParamsString: string, sectionId: string) =>
      ['qa-reports-for-agency-list-inspections/get', searchParamsString, sectionId] as const,
  },

  metrics: {
    ctpAudits: (id: string, dateFrom: string, dateTo: string) => ['metrics/ctp_audits', id, dateFrom, dateTo] as const,
    inspectionsStats: (id: string, dateFrom: string, dateTo: string) =>
      ['metrics/inspections_stats', id, dateFrom, dateTo] as const,
    qaReportsStats: (id: string, quarterId: string) => ['metrics/qa_reports_stats', id, quarterId] as const,
    geolocationStats: (id: string, dateFrom: string, dateTo: string) =>
      ['metrics/geolocation_stats', id, dateFrom, dateTo] as const,
    permitsStats: (id: string, dateFrom: string, dateTo: string) =>
      ['metrics/permits_stats', id, dateFrom, dateTo] as const,
    projectsStats: (id: string, dateFrom: string, dateTo: string) =>
      ['metrics/projects_stats', id, dateFrom, dateTo] as const,
  },

  payments: {
    methods: (params?: AppNS.Params) => ['paymentMethods/get', ...(params ? [params] : [])] as const,
    merchantAccount: (id: string, stripeAccountName: string) => ['merchantAccounts', id, stripeAccountName] as const,
    merchantAccountById: (id: string) => ['merchantAccounts', id] as const,
    merchantAccountDashboardLink: (id: string) => ['merchantAccounts/dashboardLink', id] as const,
    checkUser: () => ['checkUserPayments'] as const,
    agencyList: (searchParamsString: string) => ['agency-list-payments/get', searchParamsString] as const,
    agencyPayouts: (searchParamsString: string) => ['agency-list-payouts/get', searchParamsString] as const,
    agencyInvoices: (searchParamsString: string) => ['agency-list-invoices/get', searchParamsString] as const,
    clientList: (searchParamsString: string) => ['client-list-payments/get', searchParamsString] as const,
  },

  bookings: {
    clientList: (searchParamsString: string, sectionId: string) =>
      ['client-list-bookings/get', searchParamsString, sectionId] as const,
    clientListForPayments: () => ['client-list-bookings/get-for-payment-method'] as const,
    agencyList: (searchParamsString: string, sectionId: string) =>
      ['agency-list-bookings/get', searchParamsString, sectionId] as const,
    backofficeProjectsList: (searchParamsString: string) =>
      ['backoffice-projects-list/get', searchParamsString] as const,
    clientAll: () => ['client/bookings-all'] as const,
    clientJobs: () => ['client/jobs-all'] as const,
    clientInspections: () => ['client/inspections-all'] as const,
  },

  certificates: {
    all: () => ['certificates/get'] as const,
    byProject: (projectId: string) => ['certificates', projectId] as const,
  },

  permits: {
    swoCheck: (projectId: string) => ['swoCheck', projectId] as const,
    checkDuplicates: (permitIdsJoined: string) => ['checkDuplicates', permitIdsJoined] as const,
  },

  accela: {
    owners: () => ['accela/owners'] as const,
    equipment: () => ['accela/equipment'] as const,
  },

  ctpRecords: {
    list: (searchParamsString: string) => ['ctp-records/get', searchParamsString] as const,
  },

  csvExports: {
    applications: () => ['csv_export_applications'] as const,
    agencies: () => ['csv_export_agencies'] as const,
    inspectorProfiles: () => ['csv_export_inspector_profiles'] as const,
    projects: () => ['csv_export_projects'] as const,
    tickets: () => ['csv_export_tickets'] as const,
    changesLog: (applicationId: string) => ['csv_export_changes_log', applicationId] as const,
  },

  block: {
    all: () => ['blocks'] as const,
    lists: () => [...queryKeys.block.all(), 'list'] as const,
    list: (searchParamsString: string) => [...queryKeys.block.lists(), searchParamsString] as const,
    check: (userId: string) => [...queryKeys.block.all(), 'get', userId] as const,
  },
};
